/**
 * Session storage helpers for chat persistence.
 *
 * Keys are prefixed with user ID for per-salesperson isolation so that
 * different salespeople logging into the same browser don't see each
 * other's chat history.
 */

import type { ChatMessage } from "../hooks/useSSEChat";

const SESSION_MESSAGES_KEY_PREFIX = "cm_chat_msgs_";
const SESSION_CONV_ID_KEY_PREFIX = "cm_chat_convid_";

export function getSessionMessagesKey(userId: string): string {
  return `${SESSION_MESSAGES_KEY_PREFIX}${userId}`;
}

export function getSessionConvIdKey(userId: string): string {
  return `${SESSION_CONV_ID_KEY_PREFIX}${userId}`;
}

/** Write messages + conversationId to sessionStorage for the given user. */
export function persistToSession(
  userId: string,
  messages: ChatMessage[],
  conversationId: string | null,
): void {
  try {
    const key = getSessionMessagesKey(userId);
    // Only persist non-streaming messages to avoid partial state
    const safe = messages.filter((m) => !m.isStreaming);
    sessionStorage.setItem(key, JSON.stringify(safe));

    const convKey = getSessionConvIdKey(userId);
    if (conversationId) {
      sessionStorage.setItem(convKey, conversationId);
    } else {
      sessionStorage.removeItem(convKey);
    }
  } catch {
    // sessionStorage full or unavailable — non-critical
  }
}

/** Read persisted messages from sessionStorage for the given user. */
export function loadFromSession(
  userId: string,
): { messages: ChatMessage[]; conversationId: string | null } | null {
  try {
    const key = getSessionMessagesKey(userId);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const messages = JSON.parse(raw) as ChatMessage[];
    const conversationId = sessionStorage.getItem(getSessionConvIdKey(userId));
    return { messages, conversationId };
  } catch {
    return null;
  }
}

/** Clear persisted chat state for a user (on explicit clear). */
export function clearSession(userId: string): void {
  try {
    sessionStorage.removeItem(getSessionMessagesKey(userId));
    sessionStorage.removeItem(getSessionConvIdKey(userId));
  } catch {
    // non-critical
  }
}
